import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { BaseFieldSet } from "../../../components/formfields/fieldset/styled";
import { BaseInput } from "../../../components/formfields/input/styled";
import { BaseLabel } from "../../../components/formfields/label/styled";
import { BaseLegend } from "../../../components/formfields/legend/styled";
import { AuthLayout } from "../../../containers/layout/auth";
import { PasswordRecoveryWrapper } from "./styled";
import { BaseButton } from "../../../components/button/styled";
import { Fragment } from "react/jsx-runtime";
import { BaseAlertModal } from "../../../components/modals/alert";
import { resetPasswordService } from "../../../utils/apis/authentication/resetPassword";
import { requestAuthOTPService } from "../../../utils/apis/authentication/requestOTP";
import { BaseFormModal } from "../../../components/modals/form";
import { BaseFormModalRefType } from "../../../types/app.type";
import { verifyOTPService } from "../../../utils/apis/authentication/verifyOTP";
import Cookies from "universal-cookie";

export const PasswordRecovery = () => {
    const cookies = new Cookies();

    const { action } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const formModalRef = useRef<BaseFormModalRefType>(null);

    const [isOTPFormModalOpen, setIsOTPFormModalOpen] = useState(false);
    const [isPasswordResetSuccessModalOpen, setIsPasswordResetSuccessModalOpen] = useState(false);
    const [isOTPVerificationSuccessModalOpen, setIsOTPVerificationSuccessModalOpen] = useState(false);
    const [isOTPFormLoading, setIsOTPFormLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [OTPFormError, setOTPFormError] = useState<string | null>(null);
    const [formDetails, setFormDetails] = useState(action === "resetpassword" ? {
        newPassword: "",
        confirmPassword: "",
    } : {
        email: "",
    });

    useEffect(() => {
        setError(null);
    }, [location]);

    const closeSuccessModal = () => {
        setIsOTPVerificationSuccessModalOpen(true)
    };

    const handleOTPFormModalPersist = () => {
        if (isLoading) return;
        // reset error to default
        setError(null);
        return setIsOTPFormModalOpen(true);
    };

    const handleRedirect = () => {
        switch (action) {
            case "forgotpassword":
                navigate("/passwordrecovery/resetpassword");
                break;
            default:
                navigate("/");
                break;
        };
        setError(null);
        setIsOTPVerificationSuccessModalOpen(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOTPVerification = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setOTPFormError(null);
        setIsOTPFormLoading(true);
        const payload = {
            requester: formDetails.email,
            OTP: formModalRef.current?.getOTP(),
        };
        try {
            const response = await verifyOTPService(payload);
            if (response.status === "success") {
                setIsLoading(false);
                cookies.set("RESET_PASSWORD_TOKEN", response.token, {
                    path: "/",
                    expires: new Date(Date.now() + 5 * 60 * 1000) // 5 minutes from now
                });
                setIsOTPFormModalOpen(false);
                setIsOTPVerificationSuccessModalOpen(true);
            } else {
                setIsOTPFormLoading(false);
                setOTPFormError('OTP verification failed. Please try again.');
            }
        } catch (error: any) {
            setIsOTPFormLoading(false);
            setOTPFormError(`OTP verification failed. ${error.message}`);
            console.error('Authentication failed:', error);
        }
    };

    const handleFormSubmission = async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLParagraphElement, MouseEvent>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            let response: any;
            switch (action) {
                case "forgotpassword":
                    response = await requestAuthOTPService(formDetails);
                    break;
                case "resetpassword":
                    const token = cookies.get("RESET_PASSWORD_TOKEN");
                    if (!token) {
                        setIsLoading(false);
                        setError('OTP Expired. Please request a new OTP');
                        return;
                    };
                    response = await resetPasswordService(token, formDetails);
                    break;
                default:
                    break;
            };
            if (response.status === "success") {
                setIsLoading(false);
                switch (action) {
                    case "forgotpassword":
                        setIsOTPFormModalOpen(true);
                        break;
                    case "resetpassword":
                        cookies.remove("RESET_PASSWORD_TOKEN", { path: '/' });
                        setIsPasswordResetSuccessModalOpen(true);
                        break;
                    default:
                        break;
                };
            } else {
                setIsLoading(false);
                setError('Authentication failed. Please check your credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Authentication failed. ${error.message}`);
            console.error('Authentication failed:', error);
        };
    };

    return (
        <AuthLayout>
            <PasswordRecoveryWrapper
                onSubmit={handleFormSubmission}
            >
                <BaseFormModal
                    ref={formModalRef}
                    title={"Verify OTP"}
                    location={"password-recovery"}
                    open={isOTPFormModalOpen}
                    error={OTPFormError}
                    isLoading={isOTPFormLoading}
                    className="OTP-form-modal"
                    resendOTP={handleFormSubmission}
                    handleClose={handleOTPFormModalPersist}
                    handleSubmit={handleOTPVerification}
                />
                <BaseAlertModal
                    open={isOTPVerificationSuccessModalOpen}
                    handleClose={closeSuccessModal}
                    title="Verification Successful"
                    message="Proceed to reset password"
                    callToAction="Continue"
                    handleCallToAction={handleRedirect}
                />
                <BaseAlertModal
                    open={isPasswordResetSuccessModalOpen}
                    handleClose={closeSuccessModal}
                    title="Password Reset Successful"
                    message="Proceed to login"
                    callToAction="Continue"
                    handleCallToAction={handleRedirect}
                />
                <BaseLegend>Forgot password?</BaseLegend>
                <Typography
                    variant="subtitle1"
                    fontFamily={"Inter"}
                    fontWeight={400}
                    fontSize={20}
                    lineHeight={"normal"}
                    color={"#948E8E"}
                    marginBlock={"calc(var(--basic-margin)/4)"}
                    whiteSpace={"normal"}
                >
                    {(action === "resetpassword") ? "Please enter your new password" : "Please type in the email address linked to your Guib account to reset your password"}
                </Typography>
                {(action === "resetpassword") ? (
                    <Fragment>
                        <BaseFieldSet>
                            <BaseLabel>NEW PASSWORD</BaseLabel>
                            <BaseInput
                                required
                                name="newPassword"
                                type="password"
                                placeholder="Enter new password"
                                onChange={(e) => handleChange(e)}
                            />
                        </BaseFieldSet>
                        <BaseFieldSet>
                            <BaseLabel>CONFIRM PASSWORD</BaseLabel>
                            <BaseInput
                                required
                                name="confirmPassword"
                                type="password"
                                placeholder="Re-enter new password"
                                onChange={(e) => handleChange(e)}
                            />
                        </BaseFieldSet>
                    </Fragment>
                ) : (
                    <BaseFieldSet>
                        <BaseLabel>EMAIL</BaseLabel>
                        <BaseInput
                            required
                            name="email"
                            type="email"
                            placeholder="info@guibcloud.com"
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                )}
                {error && <Typography
                    fontFamily={"Inter"}
                    fontWeight={"600"}
                    fontSize={16}
                    lineHeight={"normal"}
                    color={"#FF0000"}
                    whiteSpace={"normal"}
                >
                    {error}
                </Typography>}
                < Box
                    component={"div"}
                    className="call-to-action"
                >
                    {(action === "resetpassword") ? (
                        <Fragment>
                            <Typography
                                variant="subtitle1"
                                fontFamily={"Inter"}
                                fontWeight={600}
                                fontSize={16}
                                lineHeight={"normal"}
                                color={"inherit"}
                                marginBlock={"calc(var(--basic-margin)/4)"}
                            >
                                Password must:
                            </Typography>
                            <ul>
                                <li>
                                    <Typography
                                        variant="subtitle1"
                                        fontFamily={"Inter"}
                                        fontWeight={400}
                                        fontSize={16}
                                        lineHeight={"normal"}
                                        color={"#9B9595"}
                                    >
                                        Contain at least 8 characters
                                    </Typography>
                                </li>
                                <li>
                                    <Typography
                                        variant="subtitle1"
                                        fontFamily={"Inter"}
                                        fontWeight={400}
                                        fontSize={16}
                                        lineHeight={"normal"}
                                        color={"#9B9595"}
                                    >
                                        Contain at least one number
                                    </Typography>
                                </li>
                                <li>
                                    <Typography
                                        variant="subtitle1"
                                        fontFamily={"Inter"}
                                        fontWeight={400}
                                        fontSize={16}
                                        lineHeight={"normal"}
                                        color={"#9B9595"}
                                    >
                                        Match with your confirm password
                                    </Typography>
                                </li>
                            </ul>
                        </Fragment>
                    ) : (
                        <Typography
                            variant="subtitle1"
                            fontFamily={"Inter"}
                            fontWeight={600}
                            fontSize={16}
                            lineHeight={"normal"}
                            color={"inherit"}
                            marginBlock={"calc(var(--basic-margin)/4)"}
                        >
                            Remember password? <Typography
                                component={"span"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"#791377"}
                                onClick={() => navigate("/")}
                                sx={{
                                    cursor: "pointer"
                                }}
                            >
                                Back to Login
                            </Typography>
                        </Typography>
                    )}
                    <BaseButton
                        type="submit"
                        variant="contained"
                        sx={{
                            width: "100%"
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress color="inherit" className="loader" />
                        ) : (
                            <Typography
                                variant={"button"}
                                fontFamily={"inherit"}
                                fontWeight={"inherit"}
                                fontSize={"inherit"}
                                lineHeight={"inherit"}
                                color={"inherit"}
                                textTransform={"inherit"}
                            >
                                Reset
                            </Typography>
                        )}
                    </BaseButton>
                </Box>
            </PasswordRecoveryWrapper >
        </AuthLayout >
    )
}